import router from 'next/router';
import jwtDecode from 'jwt-decode';

import { login } from 'legacy/actions/userActions';

import storage from 'legacy/common/utils/storage';
import { setSentryUser } from 'legacy/common/utils/sentry';
import { STORAGE_KEYS } from 'legacy/common/constants/storage';

import { UserMe } from 'legacy/user/types';
import { LoginResponse } from './types';

import { store } from '../store';
import { trackUserSession } from 'legacy/common/utils/amplitude';
import { trackUserSessionOnGa } from 'legacy/common/utils/ga';

interface TokenData {
  _id: string;
  exp: string;
  iat: string;
}

export function decodeJWT(jwt: string) {
  return jwtDecode<TokenData>(jwt);
}

function setToken(token: string) {
  const jwt = token.split('JWT ')[1];
  const { exp } = decodeJWT(jwt);

  storage.setItem(STORAGE_KEYS.AUTH_TOKEN, jwt);

  document.cookie = `token=${jwt}; path=/; Max-Age=${exp}`;
}

type Params = {
  from: string;
};
export function handleRedirect(user: UserMe, params?: Params) {
  const from = params?.from || router?.query?.from;
  let query = null;
  if (from) {
    query = {
      from
    };
  }

  if (
    user?.signupRequirements?.includes('email') ||
    user?.signupRequirements?.includes('name') ||
    user?.signupRequirements?.includes('country')
  ) {
    router.push({
      pathname: '/login/updateProfile',
      query
    });
    return;
  }

  if (user?.signupRequirements?.includes('emailVerified')) {
    router.push({
      pathname: '/login/verifyEmail',
      query
    });
    return;
  }

  if (
    typeof from === 'string' &&
    from &&
    !from.startsWith('/login') &&
    !from.startsWith('/register')
  ) {
    router.push(from);
    return;
  }

  router.push('/dashboard');
}

export function handleLogin(data: LoginResponse, params?: Params) {
  trackUserSession(data.user._id);
  trackUserSessionOnGa(data.user._id);

  setToken(data.token);
  setSentryUser(data.user);

  handleRedirect(data.user, params);
  store.dispatch(login(data));
}

/**
 * Copy of handleLogin without the redirect.
 * Used to support simple sign on where we want to preserve the active view after authentication.
 */
export function handleLoginNoRedirect(data: LoginResponse) {
  trackUserSession(data.user._id);
  trackUserSessionOnGa(data.user._id);

  setToken(data.token);
  setSentryUser(data.user);

  store.dispatch(login(data));
}

export function parsePhoneNumber(phoneNumber: string) {
  return phoneNumber.replace(/\D/g, '');
}

// https://stackoverflow.com/a/16702965
export const phoneNumberRegex =
  /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

// https://github.com/jquense/yup/issues/743#issuecomment-884851222
export const emailRegex =
  /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

type LoginMethod = 'phone' | 'email' | 'unknown';

export const isPhoneNumber = (value: string): boolean =>
  phoneNumberRegex.test(value);
export const isEmailAddress = (value: string): boolean =>
  emailRegex.test(value);

export const getLoginMethod = (value: string): LoginMethod => {
  if (isPhoneNumber(value)) {
    return 'phone';
  }

  if (isEmailAddress(value)) {
    return 'email';
  }

  return 'unknown';
};
